import {downloadFileAsync} from '@domain/file-download'
import {TaskDetail} from '@features/rcfa-list'
import {DataTable, DataTableRef, TableSizedColumns} from '@hconnect/common/maintain-datatable'
import {OrderByApiProperty, useListQuerySync, ApiFilter} from '@hconnect/common/maintain-filters'
import {useTheme} from '@mui/material'
import React, {ReactNode, useRef, useState} from 'react'

import {useGetTasksList} from '../../hooks/tasks/useGetTasksList'
import {useTasksListExport} from '../../hooks/tasks/useTasksListExport'
import {TaskListColumn, TaskListItem, TaskListProperty} from '../../types/taskList.types'

export const TasksListTable: React.FC<{
  page: number
  rowsNumber: number
  widthKey: string
  customColumnRender: (
    columnName: string,
    data: TaskListItem,
    widthKey?: string | undefined
  ) => ReactNode
  handleOrderToggle: (col) => void
  availableColumns: TaskListColumn[]
  columns?: TableSizedColumns
  filters?: ApiFilter[]
  order?: OrderByApiProperty[]
}> = ({
  page,
  rowsNumber,
  widthKey,
  columns,
  customColumnRender,
  filters,
  order,
  handleOrderToggle,
  availableColumns
}) => {
  const theme = useTheme()
  const [activeTask, setActiveTask] = useState<TaskListItem | undefined>(undefined)
  const tableRef = useRef<DataTableRef | null>(null)
  const {data, isPending: isLoading} = useGetTasksList(page, rowsNumber, filters, order)
  const {handlePageChange, handleRowsPerPageChange} = useListQuerySync({
    filters,
    order,
    filtersEnabled: true,
    orderEnabled: true
  })
  const {mutateAsync: downloadAsync, isPending: isDownloading} = useTasksListExport()

  const downloadExcel = () =>
    void downloadFileAsync(
      async () => downloadAsync({page, filters, order}),
      `rcfa-tasks_${new Date().toISOString()}.xlsx`
    )

  return (
    <DataTable<TaskListProperty, TaskListItem>
      ref={tableRef}
      paginationSettings={{
        page,
        rowsNumber,
        handlePageChange,
        handleRowsPerPageChange
      }}
      tableHeaderSettings={{
        showDescription: true,
        download: {
          showDownload: true,
          downloadFunction: downloadExcel,
          isDownloading
        }
      }}
      orderSettings={{
        handleOrderToggle,
        order
      }}
      widthKey={widthKey}
      columns={columns}
      customColumnRender={customColumnRender}
      tableId={'tasks-list'}
      headerName={'label.tasks'}
      availableColumns={availableColumns}
      hoverColor={theme.palette.grey.A100}
      data={data}
      isLoadingData={isLoading}
      setActiveItem={setActiveTask}
      detailComponent={
        activeTask && (
          <TaskDetail
            analysisTitle={activeTask.analysisTitle}
            nodeTitle={activeTask.nodeTitle}
            nodeId={activeTask.nodeId}
            analysisId={activeTask.analysisId ? activeTask.analysisId.toString() : undefined}
            taskId={activeTask.id}
            closeAction={() => {
              tableRef.current?.clearDetail()
              setActiveTask(undefined)
            }}
            hideBackButton
            disablePadding
          />
        )
      }
    />
  )
}
